import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface CreateAccountFormData {
  email: string;
  password: string;
  confirmPassword: string;
  uniqueCode: string;
}

type CodeStatus = 'loading' | 'invalid' | 'used' | 'valid';

const CreateAccount: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [codeStatus, setCodeStatus] = useState<CodeStatus>('loading');
  const [uniqueCodeFromUrl, setUniqueCodeFromUrl] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CreateAccountFormData>();

  const password = watch('password');

  useEffect(() => {
    const code = searchParams.get('code');
    if (!code) {
      setCodeStatus('invalid');
      setError('Invalid link. Please use the link from your verification email.');
      return;
    }
    setUniqueCodeFromUrl(code);
    setValue('uniqueCode', code);

    let cancelled = false;
    apiService
      .checkSignupCode(code)
      .then(({ valid, used, email }) => {
        if (cancelled) return;
        if (!valid) setCodeStatus('invalid');
        else if (used) setCodeStatus('used');
        else {
          setCodeStatus('valid');
          if (email) setValue('email', email);
        }
      })
      .catch(() => {
        if (!cancelled) setCodeStatus('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [searchParams, setValue]);

  const onSubmit = async (data: CreateAccountFormData) => {
    try {
      setLoading(true);
      setError('');

      // Validate password confirmation
      if (data.password !== data.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      // Call API to create account
      const response = await apiService.createAccount({
        email: data.email,
        password: data.password,
        unique_code: data.uniqueCode,
      });

      // Login user automatically
      await login({ username: data.email, password: data.password });

      toast.success('Account created successfully!');
      navigate('/my-courses');
    } catch (err: any) {
      const raw = err.response?.data?.detail || err.message || 'Account creation failed';
      const message =
        typeof raw === 'string' && raw.toLowerCase().includes('invalid token')
          ? 'Please use the link from your verification email and try again.'
          : raw;
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (codeStatus === 'loading') {
    return (
      <Box sx={{ py: 8 }}>
        <Container maxWidth="sm">
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography color="text.secondary">Checking your link...</Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  if (codeStatus === 'invalid') {
    return (
      <Box sx={{ py: 8 }}>
        <Container maxWidth="sm">
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Alert severity="error" sx={{ mb: 3 }}>
                Invalid link. Please use the link from your verification email.
              </Alert>
              <Button 
                variant="contained" 
                onClick={() => navigate('/')}
                sx={{
                  backgroundColor: '#10b981',
                  '&:hover': { backgroundColor: '#059669' },
                }}
              >
                Go to Home
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  if (codeStatus === 'used') {
    return (
      <Box sx={{ py: 8 }}>
        <Container maxWidth="sm">
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                You already created an account. Please log in using your account and purchase any class using that account.
              </Alert>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/login')}
                  sx={{
                    backgroundColor: '#10b981',
                    '&:hover': { backgroundColor: '#059669' },
                  }}
                >
                  Sign In
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/')}
                  sx={{
                    borderColor: '#10b981',
                    color: '#10b981',
                    '&:hover': { borderColor: '#059669', backgroundColor: 'rgba(16, 185, 129, 0.08)' },
                  }}
                >
                  Go to Home
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="sm">
        <Card>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                Create Your Account
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Complete your account setup using your unique verification code
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                      borderRadius: '12px', // Match form's border radius
                    },
                    mb: 2,
                  }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters',
                    },
                  })}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) =>
                      value === password || 'Passwords do not match',
                  })}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Unique Code"
                  {...register('uniqueCode', {
                    required: 'Unique code is required',
                  })}
                  error={!!errors.uniqueCode}
                  helperText={errors.uniqueCode?.message}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                      borderRadius: '12px', // Match form's border radius
                    },
                  }}
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
                sx={{
                  py: 1.5,
                  backgroundColor: '#10b981',
                  '&:hover': {
                    backgroundColor: '#059669',
                  },
                }}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default CreateAccount;
